import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Smartphone, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const APK_CHUNK = 5 * 1024 * 1024;
const CHUNK_RETRIES = 4;

// Per-chunk upload with retry + idempotent resume. Ek chunk network glitch par fail ho
// to poora upload ruke nahi — jise pehle 90MB par atakne wala issue fix hota hai.
async function putChunk(api, uploadId, index, blob) {
  let lastErr;
  for (let attempt = 0; attempt < CHUNK_RETRIES; attempt++) {
    try {
      const { data } = await api.post("/admin/files/upload/chunk", blob, {
        headers: { "X-Upload-Id": uploadId, "X-Chunk-Index": String(index), "Content-Type": "application/octet-stream" },
        timeout: 120000,
      });
      return data;
    } catch (err) {
      lastErr = err;
      // Agar server bole ye chunk already receive ho chuka (expected > index), skip aage badho.
      const m = /expected (\d+)/.exec(err?.response?.data?.detail || "");
      if (m && Number(m[1]) > index) return { received: Number(m[1]) };
      await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
    }
  }
  throw lastErr;
}

export function ApkCard() {
  const [apkInfo, setApkInfo] = useState(null);
  const [apkBusy, setApkBusy] = useState(false);
  const [apkProgress, setApkProgress] = useState(0);
  const [apkVersion, setApkVersion] = useState("");
  const apkRef = useRef(null);

  const loadApkInfo = () => api.get("/app/info").then((r) => setApkInfo(r.data)).catch(() => {});

  const uploadApk = async (file) => {
    const { data: init } = await api.post("/admin/files/upload/init", {
      filename: file.name, content_type: "application/vnd.android.package-archive", size: file.size, visibility: "private", tag: "app-apk",
    });
    let index = 0;
    for (let start = 0; start < file.size; start += APK_CHUNK) {
      const blob = file.slice(start, Math.min(start + APK_CHUNK, file.size));
      await putChunk(api, init.upload_id, index, blob);
      index++;
      setApkProgress(Math.round((Math.min(start + APK_CHUNK, file.size) / file.size) * 100));
    }
    const { data: doc } = await api.post("/admin/files/upload/complete", { upload_id: init.upload_id });
    await api.post("/admin/app/apk", { file_id: doc.id, version: apkVersion.trim() });
  };

  const onApkPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".apk")) { toast.error("Sirf .apk file upload karein"); return; }
    setApkBusy(true); setApkProgress(0);
    try {
      await uploadApk(f);
      toast.success("Nayi APK live ho gayi — website ka download button ab yahi file dega");
      loadApkInfo(); setApkVersion("");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setApkBusy(false); setApkProgress(0); if (apkRef.current) apkRef.current.value = ""; }
  };

  useEffect(() => { loadApkInfo(); }, []);

  return (
    <div data-testid="app-apk-card" className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <Smartphone className="text-emerald-600" size={20} />
        <div>
          <h2 className="font-bold text-base text-slate-900">Android App (APK)</h2>
          <p className="text-xs text-slate-500">Nayi app version yahan upload karein — website ka "Download for Android" button turant nayi file serve karega. Code me baar-baar APK dalne ki zaroorat nahi.</p>
        </div>
      </div>
      {apkInfo && apkInfo.available !== false && apkInfo.filename && (
        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="app-apk-current">
          <span className="font-bold text-emerald-800">Current: {apkInfo.filename}</span>
          {apkInfo.version && <span className="text-emerald-700 font-semibold">v{apkInfo.version}{apkInfo.version_code ? ` (${apkInfo.version_code})` : ""}</span>}
          {apkInfo.size_bytes ? <span className="text-emerald-700">{(apkInfo.size_bytes / 1048576).toFixed(0)} MB</span> : null}
          {apkInfo.uploaded_at && <span className="text-emerald-600">Updated: {String(apkInfo.uploaded_at).slice(0, 10)}</span>}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={apkRef} type="file" accept=".apk" className="hidden" onChange={onApkPick} data-testid="app-apk-file-input" />
        <input data-testid="app-apk-version" value={apkVersion} onChange={(e) => setApkVersion(e.target.value)} placeholder="Version (auto-detect, or override)" className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-52" />
        <button data-testid="app-apk-upload-btn" onClick={() => apkRef.current?.click()} disabled={apkBusy} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg font-bold text-xs flex items-center gap-1.5">
          {apkBusy ? <RefreshCw size={13} className="animate-spin" /> : <Smartphone size={13} />} {apkBusy ? `Uploading… ${apkProgress}%` : "Upload New APK"}
        </button>
      </div>
      {apkBusy && <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${apkProgress}%` }} data-testid="app-apk-progress" /></div>}
      <p className="text-[11px] text-slate-400 mt-2">Max 200 MB (badi file chunked upload hoti hai). Version APK se auto-detect hota hai — chahein to upar override kar sakte hain. Upload ke baad /download-app page aur download button dono nayi APK dikhayenge.</p>
    </div>
  );
}
